import { Link as RouterLink } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Grid,
  VStack,
  HStack,
  Stack,
  Badge,
  AccordionRoot,
  AccordionItem,
  AccordionItemTrigger,
  AccordionItemContent,
} from '@chakra-ui/react'
import { LanguageSelector } from '@/shared/components/LanguageSelector'
import {
  Building2,
  Users,
  FileText,
  Calendar,
  DollarSign,
  ArrowRight,
  TrendingUp,
  CheckCircle,
  ChevronDown,
  Zap,
  Shield,
  Upload,
  LayoutDashboard,
  Sparkles,
  Clock,
  BarChart3,
  Info,
  Plus,
} from 'lucide-react'
import { usePricingTiers } from '@/shared/hooks/usePricingTiers'
import { formatAllPricingTiers } from '@/shared/utils/pricingHelpers'

// Feature data for sticky scroll section
const productFeatures = [
  {
    id: 'ag',
    badge: 'Assemblées générales',
    title: (
      <>
        AGs{' '}
        <Text as="span" color="brand.600">
          100%{' '}
        </Text>
        dématérialisées
      </>
    ),
    description:
      'Préparation des résolutions, envoi des convocations dans les délais légaux, déroulé des AG en présentiel et distanciel avec outil de visio intégré. Gestion des votes de résolution et d\'élection des conseils syndicaux. Génération automatique des PV.',
    checks: ['Visio intégrée', 'Votes digitalisés', 'PV automatiques'],
  },
  {
    id: 'extranet',
    badge: 'Extranet copropriétaire',
    title: (
      <>
        Accès{' '}
        <Text as="span" color="brand.600">
          temps réel
        </Text>{' '}
        pour tous
      </>
    ),
    description:
      'Gestion électronique des documents de la résidence (devis, factures, convocations, PV, extraits de compte, appels de fonds, attestations). Déclaration et suivi des sinistres, canal de communication entre copropriétaires, suivi distanciel des AGs avec visio et votes à distance.',
    checks: ['GED complète', 'Votes à distance', 'Communication directe'],
  },
  {
    id: 'accounting',
    badge: 'Comptabilité',
    title: (
      <>
        Conformité{' '}
        <Text as="span" color="brand.600">
          réglementaire
        </Text>{' '}
        garantie
      </>
    ),
    description:
      'Application stricte de la réglementation copropriété : tantièmes, clés de répartition, génération et envoi automatique des appels de fonds. Génération des comptes et extraits de compte pour les AGs. Tout est conforme, précis et automatisé.',
    checks: ['Tantièmes automatiques', 'Appels de fonds', 'Conformité garantie'],
  },
  {
    id: 'platform',
    badge: 'Plateforme SaaS',
    title: (
      <>
        Pensée{' '}
        <Text as="span" color="brand.600">
          avec des pros
        </Text>{' '}
        du métier
      </>
    ),
    description:
      'Interface moderne et intuitive, conçue avec des professionnels du syndic. Sécurisée, dans le cloud (pas d\'installation serveur), données stockées en France et cloisonnées, 100% conforme RGPD. Accessible partout, tout le temps.',
    checks: ['Zéro installation', 'Données en France', '100% sécurisé'],
  },
]

export function LandingPage() {
  // Charger les pricing tiers depuis la DB
  const { data: pricingTiersData, isLoading: loadingPricing } = usePricingTiers()
  const pricingPlans = pricingTiersData ? formatAllPricingTiers(pricingTiersData) : []

  // Theme colors (light mode only)
  const bg = {
    primary: 'white',
    secondary: 'gray.50',
    tertiary: 'gray.100',
    card: 'white',
    cardHover: 'gray.50',
    mockup: 'gray.100',
    mockupCard: 'white',
  }

  const text = {
    primary: 'gray.900',
    secondary: 'gray.600',
    tertiary: 'gray.500',
  }

  const border = {
    default: 'gray.200',
    subtle: 'gray.300',
    footer: 'gray.200',
  }

  return (
    <Box bg={"white"} color={"gray.900"}>
      {/* ========================================
          HERO SECTION
      ======================================== */}
      <Box
        position="relative"
        overflow="hidden"
        minH="100vh"
        display="flex"
        alignItems="center"
      >

        <Container maxW="container.xl" position="relative" zIndex={1} py={{ base: 20, md: 32 }} px={{ base: 6, md: 12, lg: 16 }}>
          <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={{ base: 12, lg: 16 }} alignItems="center">
            {/* LEFT SIDE - Content */}
            <VStack align={{ base: 'center', lg: 'start' }} gap={8} textAlign={{ base: 'center', lg: 'left' }}>
              {/* Main Heading */}
              <Heading
                as="h1"
                fontSize={{ base: '5xl', md: '6xl', lg: '7xl' }}
                fontWeight="bold"
                lineHeight="1.1"
                letterSpacing="tight"
                color={"gray.900"}
              >
                La{' '}
                <Text
                  as="span"
                  color="brand.600"
                  fontWeight="bold"
                >
                  transparence
                </Text>{' '}
                que vos copropriétaires réclament !
              </Heading>

              <Text
                fontSize={{ base: 'xl', md: '2xl' }}
                color={"gray.600"}
                maxW="600px"
                lineHeight="tall"
              >
                Kopro centralise assemblées générales, comptabilité et commnunication. Vos copropriétaires voient tout. Vous gagnez en sérénité et en efficacité.
              </Text>

              {/* CTAs */}
              <Stack direction={{ base: 'column', sm: 'row' }} gap={4} w={{ base: 'full', sm: 'auto' }}>
                <Box position="relative">
                  <Badge
                    position="absolute"
                    top="-10px"
                    right="-10px"
                    bg="white"
                    color="brand.600"
                    fontSize="xs"
                    px={2}
                    py={1}
                    borderRadius="full"
                    border="1px solid"
                    borderColor="brand.600"
                    zIndex={1}
                    boxShadow="0 2px 8px rgba(6, 95, 70, 0.15)"
                  >
                    Founders Program
                  </Badge>
                  <RouterLink to="/founder">
                    <Button
                      size="xl"
                      colorPalette="brand"
                      bg="brand.600"
                      color="white"
                      _hover={{ bg: 'brand.700', transform: 'translateY(-2px)' }}
                      px={10}
                      py={8}
                      fontSize="lg"
                      boxShadow="0 0 40px rgba(16, 185, 129, 0.3)"
                      transition="all 0.2s"
                      w={{ base: 'full', sm: 'auto' }}
                    >
                      Voir Kopro en action
                      <ArrowRight size={20} style={{ marginLeft: '8px' }} />
                    </Button>
                  </RouterLink>
                </Box>
                <a href="#pricing">
                  <Button
                    size="xl"
                    variant="outline"
                    borderColor={border.subtle}
                    color={"gray.900"}
                    _hover={{ bg: bg.tertiary, borderColor: "gray.200" }}
                    px={10}
                    py={8}
                    fontSize="lg"
                    w={{ base: 'full', sm: 'auto' }}
                  >
                    Voir les tarifs
                  </Button>
                </a>
              </Stack>

              {/* Trust Indicators */}
              <HStack
                gap={8}
                pt={4}
                color={text.tertiary}
                fontSize="sm"
                display={{ base: 'none', md: 'flex' }}
                justify={{ base: 'center', lg: 'start' }}
              >
              <HStack gap={2}>
                <CheckCircle size={18} />
                <Text>Essai gratuit 7 jours</Text>
              </HStack>
              <HStack gap={2}>
                <CheckCircle size={18} />
                <Text>Sans engagement</Text>
              </HStack>
              <HStack gap={2}>
                <CheckCircle size={18} />
                <Text>Support dédié</Text>
              </HStack>
            </HStack>
            </VStack>

            {/* RIGHT SIDE - Illustration */}
            <Box
              display={{ base: 'none', lg: 'block' }}
              position="relative"
            >
              <img
                src="/assets/digital-meeting.svg"
                alt="Digital Meeting Illustration"
                style={{
                  width: '100%',
                  height: 'auto',
                  maxWidth: '600px',
                  margin: '0 auto',
                  display: 'block'
                }}
              />
            </Box>
          </Grid>

          {/* Interactive Product Demo - Below hero on all screens */}
          <Box mt={32}>
            <InteractiveDashboardMockup  bg={bg} text={text} border={border} />
          </Box>
        </Container>
      </Box>

      {/* ========================================
          VALUE PROPOSITION SECTION
      ======================================== */}
      <Box py={{ base: 20, md: 32 }} bg={"gray.50"} position="relative" overflow="hidden">
        {/* Gradient Background */}
        <Box
          position="absolute"
          inset={0}
          bg={"radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.1), transparent 70%)"}
        />
        {/* Grid Pattern */}
        <Box
          position="absolute"
          inset={0}
          bgImage="linear-gradient(to right, rgba(16, 185, 129, 0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(16, 185, 129, 0.05) 1px, transparent 1px)"
          bgSize="60px 60px"
          opacity={0.3}
        />

        <Container maxW="container.lg" px={{ base: 6, md: 12, lg: 16 }} position="relative" zIndex={1}>
          <VStack gap={16} textAlign="center">
            {/* Main Value Prop */}
            <VStack gap={6} maxW="900px">
              <Badge
                size="md"
                px={4}
                py={2}
                borderRadius="full"
                bg="brand.100"
                color="brand.600"
                border="1px solid"
                borderColor="brand.200"
                fontSize="sm"
                fontWeight="semibold"
              >
                <HStack gap={2}>
                  <Sparkles size={16} />
                  <Text>La différence Kopro</Text>
                </HStack>
              </Badge>

              <Heading
                as="h2"
                fontSize={{ base: '4xl', md: '5xl', lg: '6xl' }}
                fontWeight="bold"
                lineHeight="1.1"
                color={"gray.900"}
              >
                Digitalisez l'intégralité de vos{' '}
                <Text as="span" color="brand.600">
                  processus
                </Text>{' '}
                de copropriété
              </Heading>

              <Text fontSize={{ base: 'lg', md: 'xl' }} color={"gray.600"} maxW="800px" lineHeight="tall">
                De la convocation d'AG à la comptabilité, Kopro transforme chaque processus papier en workflow digital automatisé.
                Gagnez en efficacité, en transparence et en professionnalisme.
              </Text>
            </VStack>

            {/* 3 étapes simples */}
            <Grid
              templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
              gap={8}
              w="full"
              maxW="1000px"
            >
              <VStack gap={4} p={8} bg={"white"} borderRadius="2xl" border="1px solid" borderColor={"gray.200"}>
                <Box p={4} bg="brand.100" borderRadius="full">
                  <Upload size={40} color="var(--chakra-colors-brand-800)" strokeWidth={2} />
                </Box>
                <Heading as="h3" size="lg" color={"gray.900"} textAlign="center">
                  Importez vos données existantes
                </Heading>
                <Text color={"gray.600"} fontSize="md" lineHeight="tall" textAlign="center">
                  Migration simple et rapide de vos résidences, lots, copropriétaires et documents.
                  Notre équipe vous accompagne.
                </Text>
              </VStack>

              <VStack gap={4} p={8} bg={"white"} borderRadius="2xl" border="1px solid" borderColor={"gray.200"}>
                <Box p={4} bg="brand.100" borderRadius="full">
                  <Zap size={40} color="var(--chakra-colors-brand-800)" strokeWidth={2} />
                </Box>
                <Heading as="h3" size="lg" color={"gray.900"} textAlign="center">
                  Automatisez vos processus récurrents
                </Heading>
                <Text color={"gray.600"} fontSize="md" lineHeight="tall" textAlign="center">
                  Convocations d'AG, appels de fonds, relances, PV : tous vos workflows se déclenchent automatiquement.
                </Text>
              </VStack>

              <VStack gap={4} p={8} bg={"white"} borderRadius="2xl" border="1px solid" borderColor={"gray.200"}>
                <Box p={4} bg="brand.100" borderRadius="full">
                  <LayoutDashboard size={40} color="var(--chakra-colors-brand-800)" strokeWidth={2} />
                </Box>
                <Heading as="h3" size="lg" color={"gray.900"} textAlign="center">
                  Gérez toutes vos copros depuis une seule plateforme
                </Heading>
                <Text color={"gray.600"} fontSize="md" lineHeight="tall" textAlign="center">
                  Vue d'ensemble sur toutes vos résidences. Équipe synchronisée. Zéro outil externe nécessaire.
                </Text>
              </VStack>
            </Grid>

            {/* Social Proof / Stats
            <HStack
              gap={{ base: 8, md: 16 }}
              pt={8}
              flexWrap="wrap"
              justify="center"
            >
              <VStack gap={1}>
                <Text fontSize="4xl" fontWeight="bold" color="brand.600">
                  10 000+
                </Text>
                <Text fontSize="sm" color={"gray.600"}>
                  Copropriétaires actifs
                </Text>
              </VStack>
              <VStack gap={1}>
                <Text fontSize="4xl" fontWeight="bold" color="brand.600">
                  500+
                </Text>
                <Text fontSize="sm" color={"gray.600"}>
                  Résidences gérées
                </Text>
              </VStack>
              <VStack gap={1}>
                <Text fontSize="4xl" fontWeight="bold" color="brand.600">
                  99.9%
                </Text>
                <Text fontSize="sm" color={"gray.600"}>
                  Taux de satisfaction
                </Text>
              </VStack>
            </HStack> */}
          </VStack>
        </Container>
      </Box>

      {/* ========================================
          PRODUCT FEATURES WITH STICKY SCROLL
      ======================================== */}
      <StickyScrollSection  bg={bg} text={text} border={border} />

      {/* MOBILE VERSION - Stack Layout */}
      <Box display={{ base: 'block', lg: 'none' }} py={{ base: 16, md: 24 }} position="relative" overflow="hidden">
        <Container maxW="container.lg" px={{ base: 6, md: 12 }} position="relative" zIndex={1}>
          <VStack gap={16}>
            {/* Feature 1: AG */}
            <VStack align="stretch" gap={8}>
              <VStack align="start" gap={4}>
                <Badge
                  px={3}
                  py={1.5}
                  borderRadius="full"
                  bg="brand.100"
                  color="brand.600"
                  border="1px solid"
                  borderColor="brand.200"
                >
                  Assemblées générales
                </Badge>
                <Heading as="h2" fontSize={{ base: '2xl', md: '3xl' }}>
                  Gestion des AG{' '}
                  <Text as="span" color="brand.600">
                    100% digitalisée
                  </Text>
                </Heading>
                <Text fontSize="md" color={"gray.600"} lineHeight="tall">
                  Génération automatique des convocations, votes en ligne sécurisés, décompte
                  instantané, rédaction du PV automatisée.
                </Text>
              </VStack>
              <Box p={6} bg={"white"} borderRadius="2xl" border="1px solid" borderColor={"gray.200"}>
                <VStack align="stretch" gap={4}>
                  <HStack justify="space-between" p={4} bg={bg.mockup} borderRadius="lg">
                    <HStack gap={3}>
                      <Calendar size={20} color="#10b981" />
                      <Text fontWeight="semibold" fontSize="sm">
                        AG Résidence Les Jardins
                      </Text>
                    </HStack>
                    <Badge colorPalette="green" size="sm">
                      En cours
                    </Badge>
                  </HStack>
                  <Grid templateColumns="repeat(3, 1fr)" gap={3}>
                    <Box p={3} bg={bg.mockupCard} borderRadius="lg" textAlign="center">
                      <Text fontSize="2xl" fontWeight="bold" color="brand.600">
                        87%
                      </Text>
                      <Text fontSize="xs" color={text.tertiary}>
                        Quorum
                      </Text>
                    </Box>
                    <Box p={3} bg={bg.mockupCard} borderRadius="lg" textAlign="center">
                      <Text fontSize="2xl" fontWeight="bold">
                        142
                      </Text>
                      <Text fontSize="xs" color={text.tertiary}>
                        Participants
                      </Text>
                    </Box>
                    <Box p={3} bg={bg.mockupCard} borderRadius="lg" textAlign="center">
                      <Text fontSize="2xl" fontWeight="bold" color="green.400">
                        12
                      </Text>
                      <Text fontSize="xs" color={text.tertiary}>
                        Résolutions
                      </Text>
                    </Box>
                  </Grid>
                </VStack>
              </Box>
            </VStack>

            {/* Feature 2: Accounting */}
            <VStack align="stretch" gap={8}>
              <VStack align="start" gap={4}>
                <Badge
                  px={3}
                  py={1.5}
                  borderRadius="full"
                  bg="brand.100"
                  color="brand.600"
                  border="1px solid"
                  borderColor="brand.200"
                >
                  Comptabilité
                </Badge>
                <Heading as="h2" fontSize={{ base: '2xl', md: '3xl' }}>
                  Génération automatique des{' '}
                  <Text as="span" color="brand.600">
                    appels de fonds
                  </Text>
                </Heading>
                <Text fontSize="md" color={"gray.600"} lineHeight="tall">
                  Budget prévisionnel, répartition par tantièmes, génération des appels de fonds.
                  Tout est automatisé.
                </Text>
              </VStack>
              <Box p={6} bg={"white"} borderRadius="2xl" border="1px solid" borderColor={"gray.200"}>
                <VStack align="stretch" gap={4}>
                  <HStack justify="space-between" p={4} bg={bg.mockup} borderRadius="lg">
                    <HStack gap={3}>
                      <DollarSign size={20} color="#10b981" />
                      <Text fontWeight="semibold" fontSize="sm">
                        Budget 2025
                      </Text>
                    </HStack>
                    <Badge colorPalette="brand" size="sm">
                      Validé
                    </Badge>
                  </HStack>
                  <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                    <Box p={4} bg={bg.mockupCard} borderRadius="lg">
                      <Text fontSize="xs" color={text.tertiary} mb={1}>
                        Charges générales
                      </Text>
                      <Text fontSize="xl" fontWeight="bold">
                        284 750 €
                      </Text>
                    </Box>
                    <Box p={4} bg={bg.mockupCard} borderRadius="lg">
                      <Text fontSize="xs" color={text.tertiary} mb={1}>
                        Travaux votés
                      </Text>
                      <Text fontSize="xl" fontWeight="bold">
                        125 000 €
                      </Text>
                    </Box>
                  </Grid>
                </VStack>
              </Box>
            </VStack>

            {/* Feature 3: Portal */}
            <VStack align="stretch" gap={8}>
              <VStack align="start" gap={4}>
                <Badge
                  px={3}
                  py={1.5}
                  borderRadius="full"
                  bg="brand.100"
                  color="brand.600"
                  border="1px solid"
                  borderColor="brand.200"
                >
                  Portail copropriétaire
                </Badge>
                <Heading as="h2" fontSize={{ base: '2xl', md: '3xl' }}>
                  Un portail{' '}
                  <Text as="span" color="brand.600">
                    ultra-simple
                  </Text>
                </Heading>
                <Text fontSize="md" color={"gray.600"} lineHeight="tall">
                  Vos copropriétaires accèdent à leurs documents, votent en ligne, consultent leur
                  solde. Tout est intuitif.
                </Text>
              </VStack>
              <Box p={6} bg={"white"} borderRadius="2xl" border="1px solid" borderColor={"gray.200"}>
                <VStack align="stretch" gap={3}>
                  <HStack p={3} bg={bg.mockupCard} borderRadius="lg" justify="space-between">
                    <HStack gap={3}>
                      <FileText size={18} color="#10b981" />
                      <Text fontSize="sm">Mes documents</Text>
                    </HStack>
                    <Badge size="sm" colorPalette="brand">
                      24
                    </Badge>
                  </HStack>
                  <HStack p={3} bg={bg.mockupCard} borderRadius="lg" justify="space-between">
                    <HStack gap={3}>
                      <DollarSign size={18} color="#10b981" />
                      <Text fontSize="sm">Mon solde</Text>
                    </HStack>
                    <Text fontSize="sm" fontWeight="semibold" color="green.400">
                      À jour
                    </Text>
                  </HStack>
                  <HStack p={3} bg={bg.mockupCard} borderRadius="lg" justify="space-between">
                    <HStack gap={3}>
                      <Calendar size={18} color="#10b981" />
                      <Text fontSize="sm">Prochaine AG</Text>
                    </HStack>
                    <Text fontSize="sm" color={"gray.600"}>
                      15 mars 2025
                    </Text>
                  </HStack>
                </VStack>
              </Box>
            </VStack>
          </VStack>
        </Container>
      </Box>

      {/* ========================================
          FEATURES GRID - COMPACT
      ======================================== */}
      <Box py={{ base: 20, md: 32 }} bg={"gray.50"} position="relative" overflow="hidden">
        {/* Gradient Background */}
        <Box
          position="absolute"
          inset={0}
          bg={"radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.1), transparent 70%)"}
        />
        {/* Grid Pattern */}
        <Box
          position="absolute"
          inset={0}
          bgImage="linear-gradient(to right, rgba(16, 185, 129, 0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(16, 185, 129, 0.05) 1px, transparent 1px)"
          bgSize="60px 60px"
          opacity={0.3}
        />
        <Container maxW="container.lg" px={{ base: 6, md: 12, lg: 16 }} position="relative" zIndex={1}>
          <VStack gap={12}>
            <VStack gap={4} textAlign="center" maxW="700px">
              <Heading as="h2" fontSize={{ base: '3xl', md: '4xl' }} fontWeight="bold">
                Tout ce dont vous avez besoin
              </Heading>
              <Text fontSize="lg" color={"gray.600"}>
                Une plateforme complète pour gérer toutes vos copropriétés
              </Text>
            </VStack>

            <Grid
              templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
              gap={6}
            >
              {features.map((feature) => (
                <Box
                  key={feature.title}
                  p={6}
                  bg={"white"}
                  borderRadius="xl"
                  border="1px solid"
                  borderColor={"gray.200"}
                  _hover={{
                    borderColor: 'brand.300',
                    transform: 'translateY(-2px)',
                  }}
                  transition="all 0.2s"
                >
                  <Box color="brand.600" mb={4}>
                    {feature.icon}
                  </Box>
                  <Heading as="h3" size="md" mb={2} color={"gray.900"}>
                    {feature.title}
                  </Heading>
                  <Text color={"gray.600"} lineHeight="tall" fontSize="sm">
                    {feature.description}
                  </Text>
                </Box>
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* ========================================
          PRICING SECTION
      ======================================== */}
      <Box id="pricing" py={{ base: 20, md: 32 }} position="relative" overflow="hidden">
        <Container maxW="container.lg" px={{ base: 6, md: 12, lg: 16 }} position="relative" zIndex={1}>
          <VStack gap={16}>
            <VStack gap={6} textAlign="center">
              <Badge
                size="md"
                px={3}
                py={1.5}
                borderRadius="full"
                bg="brand.100"
                color="brand.600"
                border="1px solid"
                borderColor="brand.200"
              >
                Tarifs
              </Badge>
              <Heading as="h2" fontSize={{ base: '4xl', md: '5xl' }} fontWeight="bold">
                Transparent et sans surprise
              </Heading>
              <Text fontSize="xl" color={"gray.600"} maxW="600px">
                Essai gratuit sans engagement. Résiliez quand vous voulez.
              </Text>
            </VStack>

            {loadingPricing ? (
              <Text textAlign="center" color="gray.600">Chargement des formules...</Text>
            ) : pricingPlans.length === 0 ? (
              <Text textAlign="center" color="gray.600">Aucune formule disponible pour le moment.</Text>
            ) : (
              <Grid templateColumns={{ base: '1fr', lg: 'repeat(3, 1fr)' }} gap={8}>
                {pricingPlans.map((plan) => (
                <Box
                  key={plan.name}
                  p={8}
                  bg={plan.featured ? "white" : "white"}
                  borderRadius="2xl"
                  border="2px solid"
                  borderColor={plan.featured ? 'brand.600' : "gray.200"}
                  position="relative"
                  transform={plan.featured ? 'scale(1.05)' : 'scale(1)'}
                  transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                  _hover={{
                    transform: plan.featured ? 'scale(1.08) translateY(-8px)' : 'scale(1.03) translateY(-8px)',
                    borderColor: 'brand.600',
                    boxShadow: plan.featured
                      ? '0 20px 60px -15px rgba(16, 185, 129, 0.5), 0 0 0 1px rgba(16, 185, 129, 0.2)'
                      : '0 20px 60px -15px rgba(16, 185, 129, 0.3), 0 0 0 1px rgba(16, 185, 129, 0.1)',
                  }}
                >
                  {plan.featured && (
                    <Badge
                      position="absolute"
                      top={-3}
                      right={8}
                      colorPalette="brand"
                      bg="brand.600"
                      color="white"
                      px={3}
                      py={1}
                      borderRadius="full"
                      fontSize="xs"
                    >
                      ⭐ Populaire
                    </Badge>
                  )}

                  <VStack align="start" gap={6}>
                    <Box>
                      <Heading as="h3" size="lg" mb={2} color={"gray.900"}>
                        {plan.name}
                      </Heading>
                      <Text color={"gray.600"}>{plan.description}</Text>
                    </Box>

                    <Box>
                      <HStack align="baseline" gap={1}>
                        <Heading as="h4" fontSize="5xl" fontWeight="bold" color={"gray.900"}>
                          {plan.price}
                        </Heading>
                        {plan.period && (
                          <Text color={"gray.600"} fontSize="lg">
                            {plan.period}
                          </Text>
                        )}
                      </HStack>
                    </Box>

                    <a
                      href={plan.stripeLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'block', width: '100%' }}
                    >
                      <Button
                        size="lg"
                        w="full"
                        colorPalette={plan.featured ? 'brand' : undefined}
                        variant={plan.featured ? 'solid' : 'outline'}
                        bg={plan.featured ? 'brand.600' : 'transparent'}
                        color={plan.featured ? 'white' : "gray.900"}
                        borderColor={plan.featured ? undefined : border.subtle}
                        _hover={{
                          bg: plan.featured ? 'brand.700' : bg.tertiary,
                        }}
                      >
                        {plan.cta}
                      </Button>
                    </a>

                    <VStack align="start" gap={3} pt={4} w="full">
                      {plan.features.map((feature, index) => {
                        // Track if we've seen a separator
                        const hasSeenSeparator = plan.features
                          .slice(0, index)
                          .some((f) => f.type === 'separator')

                        if (feature.type === 'separator') {
                          return (
                            <Box
                              key={`separator-${index}`}
                              w="full"
                              h="1px"
                              bg="gray.200"
                              my={2}
                            />
                          )
                        }

                        if (feature.type === 'note') {
                          return (
                            <HStack key={`note-${index}`} gap={3} w="full">
                              <Info size={18} color="#10b981" style={{ flexShrink: 0 }} />
                              <Text
                                color="brand.700"
                                fontSize="sm"
                                fontWeight="medium"
                                fontStyle="italic"
                              >
                                {feature.text}
                              </Text>
                            </HStack>
                          )
                        }

                        // Inherited features (ex: "Toutes les fonctionnalités Essentiel")
                        if (feature.type === 'inherited') {
                          return (
                            <Box key={`inherited-${index}`} w="full" py={1}>
                              <Text
                                color={"gray.700"}
                                fontSize="sm"
                                fontWeight="semibold"
                                fontStyle="italic"
                              >
                                {feature.text}
                              </Text>
                            </Box>
                          )
                        }

                        // After separator, use Plus icon for unit-priced features
                        if (hasSeenSeparator) {
                          return (
                            <HStack key={`feature-${index}`} gap={3} align="start" w="full">
                              <Plus size={18} color="#6b7280" style={{ marginTop: '2px', flexShrink: 0 }} />
                              <Box flex={1}>
                                <Text color={"gray.500"} fontSize="sm">
                                  {feature.text}
                                </Text>
                                {feature.pricingNote && (
                                  <Text color={"gray.400"} fontSize="xs" mt={0.5}>
                                    {feature.pricingNote}
                                  </Text>
                                )}
                              </Box>
                            </HStack>
                          )
                        }

                        return (
                          <HStack key={`feature-${index}`} gap={3}>
                            <CheckCircle size={20} color="#10b981" />
                            <Text color={"gray.600"} fontSize="sm">
                              {feature.text}
                            </Text>
                          </HStack>
                        )
                      })}
                    </VStack>
                  </VStack>
                </Box>
              ))}
            </Grid>
            )}

            {/* Custom Plan CTA */}
            <Box textAlign="center" pt={8}>
              <Text fontSize="lg" color={"gray.600"} mb={4}>
                Besoin d'une solution sur mesure ?
              </Text>
              <RouterLink to="/contact">
                <Button
                  size="lg"
                  variant="ghost"
                  color="brand.600"
                  _hover={{ bg: bg.tertiary }}
                >
                  Contactez-nous pour un devis personnalisé
                  <ArrowRight size={20} style={{ marginLeft: '8px' }} />
                </Button>
              </RouterLink>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* ========================================
          FAQ SECTION
      ======================================== */}
      <Box py={{ base: 20, md: 32 }} bg={"gray.50"} position="relative" overflow="hidden">
        {/* Gradient Background */}
        <Box
          position="absolute"
          inset={0}
          bg={"radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.1), transparent 70%)"}
        />
        {/* Grid Pattern */}
        <Box
          position="absolute"
          inset={0}
          bgImage="linear-gradient(to right, rgba(16, 185, 129, 0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(16, 185, 129, 0.05) 1px, transparent 1px)"
          bgSize="60px 60px"
          opacity={0.3}
        />
        <Container maxW="800px" px={{ base: 6, md: 12, lg: 16 }} position="relative" zIndex={1}>
          <VStack gap={12}>
            <VStack gap={6} textAlign="center">
              <Badge
                size="md"
                px={3}
                py={1.5}
                borderRadius="full"
                bg="brand.100"
                color="brand.600"
                border="1px solid"
                borderColor="brand.200"
              >
                FAQ
              </Badge>
              <Heading as="h2" fontSize={{ base: '4xl', md: '5xl' }} fontWeight="bold">
                Questions fréquentes
              </Heading>
            </VStack>

            <VStack gap={4} align="stretch" w="full">
              <AccordionRoot collapsible>
                {faqItems.map((item, index) => (
                  <AccordionItem
                    key={index}
                    value={`faq-${index}`}
                    borderColor={"gray.200"}
                    _last={{ borderBottom: 'none' }}
                  >
                    <AccordionItemTrigger
                      py={6}
                      fontSize="lg"
                      fontWeight="medium"
                      color={"gray.900"}
                      _hover={{ color: 'brand.600' }}
                    >
                      <HStack justify="space-between" w="full">
                        <Text textAlign="left">{item.question}</Text>
                        <ChevronDown />
                      </HStack>
                    </AccordionItemTrigger>
                    <AccordionItemContent pb={6} color={"gray.600"} fontSize="md" lineHeight="tall">
                      {item.answer}
                    </AccordionItemContent>
                  </AccordionItem>
                ))}
              </AccordionRoot>
            </VStack>
          </VStack>
        </Container>
      </Box>

      {/* ========================================
          FINAL CTA SECTION
      ======================================== */}
      <Box py={{ base: 20, md: 32 }} position="relative" overflow="hidden">
        {/* Gradient Background */}
        <Box
          position="absolute"
          inset={0}
          bg={"radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.1), transparent 70%)"}
        />
        {/* Grid Pattern */}
        <Box
          position="absolute"
          inset={0}
          bgImage="linear-gradient(to right, rgba(16, 185, 129, 0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(16, 185, 129, 0.05) 1px, transparent 1px)"
          bgSize="60px 60px"
          opacity={0.3}
        />

        <Container maxW="container.md" px={{ base: 6, md: 12, lg: 16 }} position="relative" zIndex={1}>
          <VStack gap={8} textAlign="center">
            <Heading as="h2" fontSize={{ base: '4xl', md: '5xl', lg: '6xl' }} fontWeight="bold">
              Prêt à simplifier votre gestion ?
            </Heading>
            <Text fontSize={{ base: 'lg', md: 'xl' }} color={"gray.600"} maxW="600px">
              Rejoignez les syndics qui ont choisi l'efficacité et la modernité.
            </Text>
            <Stack direction={{ base: 'column', sm: 'row' }} gap={4} pt={4}>
              <Box position="relative">
                <Badge
                  position="absolute"
                  top="-10px"
                  right="-10px"
                  bg="white"
                  color="brand.600"
                  fontSize="xs"
                  px={2}
                  py={1}
                  borderRadius="full"
                  border="1px solid"
                  borderColor="brand.600"
                  zIndex={1}
                  boxShadow="0 2px 8px rgba(6, 95, 70, 0.15)"
                >
                  Founders Program
                </Badge>
                <RouterLink to="/founder">
                  <Button
                    size="xl"
                    colorPalette="brand"
                    bg="brand.600"
                    color="white"
                    _hover={{ bg: 'brand.700', transform: 'translateY(-2px)' }}
                    px={10}
                    py={8}
                    fontSize="lg"
                    boxShadow="0 0 40px rgba(16, 185, 129, 0.3)"
                  >
                    Tester Kopro
                    <ArrowRight size={20} style={{ marginLeft: '8px' }} />
                  </Button>
                </RouterLink>
              </Box>
              <RouterLink to="/contact?subject=demo">
                <Button
                  size="xl"
                  variant="outline"
                  borderColor={border.subtle}
                  color={"gray.900"}
                  _hover={{ bg: bg.tertiary }}
                  px={10}
                  py={8}
                  fontSize="lg"
                >
                  Parler à un expert
                </Button>
              </RouterLink>
            </Stack>
          </VStack>
        </Container>
      </Box>

      {/* ========================================
          FOOTER
      ======================================== */}
      <Box bg={"gray.900"} py={12} borderTop="1px solid" borderColor={border.footer}>
        <Container maxW="container.lg" px={{ base: 6, md: 12, lg: 16 }}>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(5, 1fr)' }} gap={8}>
            {/* Company Info */}
            <VStack align="start" gap={4}>
              <img
                src="/assets/logo-inline-dark.svg"
                alt="Kopro Logo"
                style={{ height: '40px', width: 'auto' }}
              />
              <Text color={"gray.400"} fontSize="sm">
                La solution de gestion de copropriétés pour les professionnels.
              </Text>
            </VStack>

            {/* Product Links */}
            <VStack align="start" gap={3}>
              <Text fontWeight="semibold" mb={2} color="white">
                Produit
              </Text>
              <RouterLink to="#features">
                <Text color={"gray.400"} fontSize="sm" _hover={{ color: 'white' }}>
                  Fonctionnalités
                </Text>
              </RouterLink>
              <RouterLink to="#pricing">
                <Text color={"gray.400"} fontSize="sm" _hover={{ color: 'white' }}>
                  Tarifs
                </Text>
              </RouterLink>
              <RouterLink to="/app/login">
                <Text color={"gray.400"} fontSize="sm" _hover={{ color: 'white' }}>
                  Se connecter
                </Text>
              </RouterLink>
            </VStack>

            {/* Support Links */}
            <VStack align="start" gap={3}>
              <Text fontWeight="semibold" mb={2} color="white">
                Support
              </Text>
              <RouterLink to="#faq">
                <Text color={"gray.400"} fontSize="sm" _hover={{ color: 'white' }}>
                  FAQ
                </Text>
              </RouterLink>
              <RouterLink to="/app/login">
                <Text color={"gray.400"} fontSize="sm" _hover={{ color: 'white' }}>
                  Documentation
                </Text>
              </RouterLink>
              <RouterLink to="/app/login">
                <Text color={"gray.400"} fontSize="sm" _hover={{ color: 'white' }}>
                  Contact
                </Text>
              </RouterLink>
            </VStack>

            {/* Legal Links */}
            <VStack align="start" gap={3}>
              <Text fontWeight="semibold" mb={2} color="white">
                Légal
              </Text>
              <RouterLink to="/app/login">
                <Text color={"gray.400"} fontSize="sm" _hover={{ color: 'white' }}>
                  Mentions légales
                </Text>
              </RouterLink>
              <RouterLink to="/app/login">
                <Text color={"gray.400"} fontSize="sm" _hover={{ color: 'white' }}>
                  CGU
                </Text>
              </RouterLink>
              <RouterLink to="/app/login">
                <Text color={"gray.400"} fontSize="sm" _hover={{ color: 'white' }}>
                  Politique de confidentialité
                </Text>
              </RouterLink>
            </VStack>

            {/* Language Selector */}
            <VStack align="start" gap={3}>
              <Text fontWeight="semibold" mb={2} color="white">
                Langue
              </Text>
              <LanguageSelector />
            </VStack>
          </Grid>

          <Box pt={8} mt={8} borderTop="1px solid" borderColor={border.footer}>
            <Text textAlign="center" color={"gray.500"} fontSize="sm">
              © {new Date().getFullYear()} Kopro. Tous droits réservés.
            </Text>
          </Box>
        </Container>
      </Box>
    </Box>
  )
}

// ========================================
// TYPES
// ========================================

type BackgroundColors = {
  primary: string
  secondary: string
  tertiary: string
  card: string
  cardHover: string
  mockup: string
  mockupCard: string
}

type TextColors = {
  primary: string
  secondary: string
  tertiary: string
}

type BorderColors = {
  default: string
  subtle: string
  footer: string
}

// ========================================
// COMPONENTS
// ========================================

interface InteractiveDashboardMockupProps {
  bg: BackgroundColors
  text: TextColors
  border: BorderColors
}

function InteractiveDashboardMockup({ bg, text, border }: InteractiveDashboardMockupProps) {
  const [activeView, setActiveView] = useState<'dashboard' | 'ag' | 'accounting' | 'portal'>('dashboard')

  const views = [
    { id: 'dashboard' as const, label: 'Vue d\'ensemble', icon: <BarChart3 size={18} /> },
    { id: 'ag' as const, label: 'Assemblées générales', icon: <Calendar size={18} /> },
    { id: 'accounting' as const, label: 'Comptabilité', icon: <DollarSign size={18} /> },
    { id: 'portal' as const, label: 'Portail copro', icon: <Users size={18} /> },
  ]

  return (
    <Box
      mt={16}
      w="full"
      maxW="1200px"
    >
      {/* Tabs Navigation */}
      <HStack
        gap={2}
        mb={6}
        justify="center"
        flexWrap="wrap"
        px={{ base: 4, md: 0 }}
      >
        {views.map((view) => (
          <Button
            key={view.id}
            size={{ base: 'sm', md: 'md' }}
            variant={activeView === view.id ? 'solid' : 'outline'}
            bg={activeView === view.id ? 'brand.600' : 'transparent'}
            color={activeView === view.id ? 'white' : "gray.900"}
            borderColor={activeView === view.id ? 'brand.600' : border.subtle}
            _hover={{
              bg: activeView === view.id ? 'brand.600' : bg.tertiary,
              borderColor: activeView === view.id ? 'brand.600' : "gray.200",
            }}
            onClick={() => setActiveView(view.id)}
            transition="all 0.2s"
          >
            <HStack gap={2}>
              {view.icon}
              <Text>{view.label}</Text>
            </HStack>
          </Button>
        ))}
      </HStack>

      {/* Mockup Container */}
      <Box
        borderRadius="2xl"
        overflow="hidden"
        border="1px solid"
        borderColor={"gray.200"}
        boxShadow={"0 25px 50px -12px rgba(0, 0, 0, 0.15)"}
        bg={"white"}
        position="relative"
        transition="all 0.3s"
      >
        {/* Mock Browser Header */}
        <Box bg={"white"} p={4} borderBottom="1px solid" borderColor={"gray.200"}>
          <HStack justify="space-between">
            <HStack gap={3}>
              <Box w={3} h={3} borderRadius="full" bg="red.500" />
              <Box w={3} h={3} borderRadius="full" bg="yellow.500" />
              <Box w={3} h={3} borderRadius="full" bg="green.500" />
            </HStack>
            <Badge colorPalette="brand" size="sm">
              {views.find(v => v.id === activeView)?.label}
            </Badge>
          </HStack>
        </Box>

        {/* Content Area */}
        <Box position="relative" minH="500px">
          {/* Dashboard View */}
          <Box
            p={8}
            bg={"gradient-to-br from-gray.50 to-white"}
            opacity={activeView === 'dashboard' ? 1 : 0}
            pointerEvents={activeView === 'dashboard' ? 'auto' : 'none'}
            position={activeView === 'dashboard' ? 'relative' : 'absolute'}
            inset={0}
            transition="opacity 0.3s ease-in-out"
          >
            <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={6}>
              <StatCard
                label="Résidences"
                value="127"
                trend="+18%"
                icon={<Building2 size={24} />}
                bg={bg}
                text={text}
                border={border}
              />
              <StatCard
                label="AG planifiées"
                value="24"
                trend="+28%"
                icon={<Calendar size={24} />}
                bg={bg}
                text={text}
                border={border}
              />
              <StatCard
                label="Copropriétaires"
                value="2,847"
                trend="+12%"
                icon={<Users size={24} />}
                bg={bg}
                text={text}
                border={border}
              />
            </Grid>

            <Box
              mt={8}
              p={6}
              bg={bg.mockup}
              borderRadius="xl"
              border="1px solid"
              borderColor={border.subtle}
            >
              <Text fontSize="sm" fontWeight="semibold" mb={4} color={"gray.600"}>
                Activité récente
              </Text>
              <VStack align="stretch" gap={3}>
                <ActivityItem
                  text="AG Résidence Les Jardins - Vote approuvé (87%)"
                  time="Il y a 5 min"
                  textColors={text}
                />
                <ActivityItem
                  text="Nouvel appel de fonds généré - Résidence Bellevue"
                  time="Il y a 12 min"
                  textColors={text}
                />
                <ActivityItem
                  text="PV assemblée générale envoyé - 142 copropriétaires"
                  time="Il y a 1h"
                  textColors={text}
                />
              </VStack>
            </Box>
          </Box>

          {/* AG View */}
          <Box
            p={8}
            bg={"gradient-to-br from-gray.50 to-white"}
            opacity={activeView === 'ag' ? 1 : 0}
            pointerEvents={activeView === 'ag' ? 'auto' : 'none'}
            position="absolute"
            inset={0}
            transition="opacity 0.3s ease-in-out"
          >
            <VStack align="stretch" gap={6}>
              <HStack justify="space-between" p={5} bg={bg.mockup} borderRadius="xl">
                <HStack gap={3}>
                  <Box p={2} bg="brand.200" borderRadius="lg">
                    <Calendar size={20} color="#10b981" />
                  </Box>
                  <VStack align="start" gap={0}>
                    <Text fontWeight="bold" fontSize="lg">
                      AG Résidence Les Jardins
                    </Text>
                    <Text fontSize="sm" color={"gray.600"}>
                      15 mars 2025 • 18h30
                    </Text>
                  </VStack>
                </HStack>
                <Badge colorPalette="green" size="lg" px={4} py={2}>
                  En cours
                </Badge>
              </HStack>

              <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                <Box p={5} bg={bg.mockupCard} borderRadius="xl" textAlign="center">
                  <Text fontSize="4xl" fontWeight="bold" color="brand.600" mb={1}>
                    87%
                  </Text>
                  <Text fontSize="sm" color={"gray.600"} fontWeight="medium">
                    Quorum atteint
                  </Text>
                </Box>
                <Box p={5} bg={bg.mockupCard} borderRadius="xl" textAlign="center">
                  <Text fontSize="4xl" fontWeight="bold" mb={1}>
                    142
                  </Text>
                  <Text fontSize="sm" color={"gray.600"} fontWeight="medium">
                    Participants
                  </Text>
                </Box>
                <Box p={5} bg={bg.mockupCard} borderRadius="xl" textAlign="center">
                  <Text fontSize="4xl" fontWeight="bold" color="green.400" mb={1}>
                    12/15
                  </Text>
                  <Text fontSize="sm" color={"gray.600"} fontWeight="medium">
                    Résolutions votées
                  </Text>
                </Box>
              </Grid>

              <VStack align="stretch" gap={3}>
                <Text fontSize="sm" fontWeight="semibold" color={"gray.600"}>
                  Votes en temps réel
                </Text>
                <Box p={4} bg={"gray.100"} borderRadius="lg">
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" fontWeight="medium">
                      Résolution n°8 - Travaux de rénovation
                    </Text>
                    <Text fontSize="sm" color="brand.600" fontWeight="bold">
                      92% Pour
                    </Text>
                  </HStack>
                  <Box w="full" h={2} bg={"gray.300"} borderRadius="full">
                    <Box w="92%" h="full" bg="brand.600" borderRadius="full" />
                  </Box>
                </Box>
              </VStack>
            </VStack>
          </Box>

          {/* Accounting View */}
          <Box
            p={8}
            bg={"gradient-to-br from-gray.50 to-white"}
            opacity={activeView === 'accounting' ? 1 : 0}
            pointerEvents={activeView === 'accounting' ? 'auto' : 'none'}
            position="absolute"
            inset={0}
            transition="opacity 0.3s ease-in-out"
          >
            <VStack align="stretch" gap={6}>
              <HStack justify="space-between" p={5} bg={bg.mockup} borderRadius="xl">
                <HStack gap={3}>
                  <Box p={2} bg="brand.200" borderRadius="lg">
                    <DollarSign size={20} color="#10b981" />
                  </Box>
                  <VStack align="start" gap={0}>
                    <Text fontWeight="bold" fontSize="lg">
                      Budget prévisionnel 2025
                    </Text>
                    <Text fontSize="sm" color={"gray.600"}>
                      Résidence Les Jardins
                    </Text>
                  </VStack>
                </HStack>
                <Badge colorPalette="brand" size="lg" px={4} py={2}>
                  Validé
                </Badge>
              </HStack>

              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <Box p={5} bg={bg.mockupCard} borderRadius="xl">
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" color={"gray.600"} fontWeight="medium">
                      Charges générales
                    </Text>
                    <Badge size="sm" colorPalette="blue">
                      Annuel
                    </Badge>
                  </HStack>
                  <Text fontSize="3xl" fontWeight="bold">
                    284 750 €
                  </Text>
                  <Text fontSize="xs" color="green.400" mt={1}>
                    +12% vs 2024
                  </Text>
                </Box>
                <Box p={5} bg={bg.mockupCard} borderRadius="xl">
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" color={"gray.600"} fontWeight="medium">
                      Travaux votés
                    </Text>
                    <Badge size="sm" colorPalette="purple">
                      Exceptionnel
                    </Badge>
                  </HStack>
                  <Text fontSize="3xl" fontWeight="bold">
                    125 000 €
                  </Text>
                  <Text fontSize="xs" color={text.tertiary} mt={1}>
                    Rénovation toiture
                  </Text>
                </Box>
              </Grid>

              <Box
                p={5}
                bg="brand.100"
                borderRadius="xl"
                border="1px solid"
                borderColor="brand.200"
              >
                <HStack justify="space-between" mb={3}>
                  <HStack gap={2}>
                    <Zap size={18} color="#10b981" />
                    <Text fontSize="sm" fontWeight="semibold" color="brand.600">
                      Appels de fonds automatiques
                    </Text>
                  </HStack>
                  <Text fontSize="sm" color={"gray.600"}>
                    T1 2025
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="sm" color={"gray.600"}>
                    142 copropriétaires • 4 échéances
                  </Text>
                  <Text fontSize="sm" fontWeight="bold" color={"gray.900"}>
                    102 438 €
                  </Text>
                </HStack>
              </Box>
            </VStack>
          </Box>

          {/* Portal View */}
          <Box
            p={8}
            bg={"gradient-to-br from-gray.50 to-white"}
            opacity={activeView === 'portal' ? 1 : 0}
            pointerEvents={activeView === 'portal' ? 'auto' : 'none'}
            position="absolute"
            inset={0}
            transition="opacity 0.3s ease-in-out"
          >
            <VStack align="stretch" gap={6}>
              <HStack justify="space-between" p={5} bg={bg.mockup} borderRadius="xl">
                <HStack gap={3}>
                  <Box p={2} bg="brand.200" borderRadius="lg">
                    <Users size={20} color="#10b981" />
                  </Box>
                  <VStack align="start" gap={0}>
                    <Text fontWeight="bold" fontSize="lg">
                      Espace copropriétaire
                    </Text>
                    <Text fontSize="sm" color={"gray.600"}>
                      M. Dupont • Lot B2-305
                    </Text>
                  </VStack>
                </HStack>
                <Badge colorPalette="green" size="lg" px={4} py={2}>
                  À jour
                </Badge>
              </HStack>

              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <Box p={5} bg={bg.mockupCard} borderRadius="xl">
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" color={"gray.600"} fontWeight="medium">
                      Solde compte
                    </Text>
                    <Box p={1} bg="green.500/20" borderRadius="md">
                      <CheckCircle size={14} color="#10b981" />
                    </Box>
                  </HStack>
                  <Text fontSize="3xl" fontWeight="bold" color="green.400">
                    0 €
                  </Text>
                  <Text fontSize="xs" color={text.tertiary} mt={1}>
                    Tous les appels réglés
                  </Text>
                </Box>
                <Box p={5} bg={bg.mockupCard} borderRadius="xl">
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" color={"gray.600"} fontWeight="medium">
                      Tantièmes
                    </Text>
                    <Badge size="sm" colorPalette="blue">
                      Lot 305
                    </Badge>
                  </HStack>
                  <Text fontSize="3xl" fontWeight="bold">
                    4,25%
                  </Text>
                  <Text fontSize="xs" color={text.tertiary} mt={1}>
                    425/10000èmes
                  </Text>
                </Box>
              </Grid>

              <VStack align="stretch" gap={3}>
                <Box
                  p={4}
                  bg={"gray.100"}
                  borderRadius="xl"
                  _hover={{ bg: "gray.200" }}
                  transition="all 0.2s"
                  cursor="pointer"
                >
                  <HStack justify="space-between">
                    <HStack gap={3}>
                      <Box p={2} bg="blue.500/20" borderRadius="lg">
                        <FileText size={18} color="#3b82f6" />
                      </Box>
                      <VStack align="start" gap={0}>
                        <Text fontSize="sm" fontWeight="semibold">
                          Mes documents
                        </Text>
                        <Text fontSize="xs" color={"gray.600"}>
                          PV, convocations, factures
                        </Text>
                      </VStack>
                    </HStack>
                    <Badge size="sm" colorPalette="brand">
                      24 documents
                    </Badge>
                  </HStack>
                </Box>

                <Box
                  p={4}
                  bg={"gray.100"}
                  borderRadius="xl"
                  _hover={{ bg: "gray.200" }}
                  transition="all 0.2s"
                  cursor="pointer"
                >
                  <HStack justify="space-between">
                    <HStack gap={3}>
                      <Box p={2} bg="purple.500/20" borderRadius="lg">
                        <Calendar size={18} color="#a855f7" />
                      </Box>
                      <VStack align="start" gap={0}>
                        <Text fontSize="sm" fontWeight="semibold">
                          Prochaine AG
                        </Text>
                        <Text fontSize="xs" color={"gray.600"}>
                          Vote en ligne disponible
                        </Text>
                      </VStack>
                    </HStack>
                    <Text fontSize="sm" fontWeight="bold" color={"gray.900"}>
                      15 mars 2025
                    </Text>
                  </HStack>
                </Box>
              </VStack>
            </VStack>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

interface StickyScrollSectionProps {
  bg: BackgroundColors
  text: TextColors
  border: BorderColors
}

function StickyScrollSection({ bg, text }: StickyScrollSectionProps) {
  const [activeFeature, setActiveFeature] = useState(0)
  const featureRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    const observers = featureRefs.current.map((ref, index) => {
      if (!ref) return null

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Quand l'élément entre dans la zone de détection
            if (entry.isIntersecting && entry.intersectionRatio > 0.3) {
              setActiveFeature(index)
            }
          })
        },
        {
          threshold: [0, 0.1, 0.3, 0.5, 0.7, 1],
          rootMargin: '-30% 0px -30% 0px',
        }
      )

      observer.observe(ref)
      return observer
    })

    return () => {
      observers.forEach((observer) => observer?.disconnect())
    }
  }, [])

  return (
    <Box
      display={{ base: 'none', lg: 'block' }}
      position="relative"
      bg={"white"}
      py={20}
    >

      <Container maxW="container.lg" px={{ base: 6, md: 12, lg: 16 }} position="relative" zIndex={1}>
        <Grid templateColumns="repeat(2, 1fr)" gap={16}>
          {/* LEFT SIDE - Textes qui scrollent */}
          <VStack align="stretch" gap={0}>
            {productFeatures.map((feature, index) => (
              <Box
                key={feature.id}
                ref={(el: HTMLDivElement | null) => (featureRefs.current[index] = el)}
                minH="100vh"
                display="flex"
                alignItems="center"
                py="20vh"
                mt={index === 0 ? "-20vh" : 0}
              >
                <VStack align="start" gap={6} w="full">
                  <Badge
                    px={3}
                    py={1.5}
                    borderRadius="full"
                    bg="brand.100"
                    color="brand.600"
                    border="1px solid"
                    borderColor="brand.200"
                  >
                    {feature.badge}
                  </Badge>
                  <Heading as="h2" fontSize="4xl" color={"gray.900"}>
                    {feature.title}
                  </Heading>
                  <Text fontSize="lg" color={"gray.600"} lineHeight="tall">
                    {feature.description}
                  </Text>
                  <HStack gap={4} flexWrap="wrap">
                    {feature.checks.map((check) => (
                      <HStack key={check} gap={2} color={"gray.600"}>
                        <CheckCircle size={20} color="#10b981" />
                        <Text fontSize="sm">{check}</Text>
                      </HStack>
                    ))}
                  </HStack>
                </VStack>
              </Box>
            ))}
          </VStack>

          {/* RIGHT SIDE - Mockup sticky qui change */}
          <Box
            position="sticky"
            top="20vh"
            alignSelf="start"
            h="70vh"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box position="relative" w="full" h="full">
              {/* Mockup 1: AG */}
              <Box
                p={8}
                bg={"gradient-to-br from-white to-gray.50"}
                borderRadius="2xl"
                border="1px solid"
                borderColor={"gray.200"}
                w="full"
                maxH="70vh"
                overflowY="auto"
                position="absolute"
                display="flex"
                alignItems="center"
                opacity={activeFeature === 0 ? 1 : 0}
                pointerEvents={activeFeature === 0 ? "auto" : "none"}
                transition="opacity 0.5s ease-in-out"
                boxShadow={"0 8px 32px rgba(0, 0, 0, 0.1)"}
              >
            <VStack align="stretch" gap={6} w="full">
              {/* Header */}
              <HStack justify="space-between" p={5} bg={bg.mockup} borderRadius="xl">
                <HStack gap={3}>
                  <Box p={2} bg="brand.200" borderRadius="lg">
                    <Calendar size={20} color="#10b981" />
                  </Box>
                  <VStack align="start" gap={0}>
                    <Text fontWeight="bold" fontSize="lg">
                      AG Résidence Les Jardins
                    </Text>
                    <Text fontSize="sm" color={"gray.600"}>
                      15 mars 2025 • 18h30
                    </Text>
                  </VStack>
                </HStack>
                <Badge colorPalette="green" size="lg" px={4} py={2}>
                  En cours
                </Badge>
              </HStack>

              {/* Stats Grid */}
              <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                <Box p={5} bg={bg.mockupCard} borderRadius="xl" textAlign="center">
                  <Text fontSize="4xl" fontWeight="bold" color="brand.600" mb={1}>
                    87%
                  </Text>
                  <Text fontSize="sm" color={"gray.600"} fontWeight="medium">
                    Quorum atteint
                  </Text>
                </Box>
                <Box p={5} bg={bg.mockupCard} borderRadius="xl" textAlign="center">
                  <Text fontSize="4xl" fontWeight="bold" mb={1}>
                    142
                  </Text>
                  <Text fontSize="sm" color={"gray.600"} fontWeight="medium">
                    Participants
                  </Text>
                </Box>
                <Box p={5} bg={bg.mockupCard} borderRadius="xl" textAlign="center">
                  <Text fontSize="4xl" fontWeight="bold" color="green.400" mb={1}>
                    12/15
                  </Text>
                  <Text fontSize="sm" color={"gray.600"} fontWeight="medium">
                    Résolutions votées
                  </Text>
                </Box>
              </Grid>

              {/* Votes en cours */}
              <VStack align="stretch" gap={3} mt={2}>
                <Text fontSize="sm" fontWeight="semibold" color={"gray.600"}>
                  Votes en temps réel
                </Text>
                <Box p={4} bg={"gray.100"} borderRadius="lg">
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" fontWeight="medium">
                      Résolution n°8 - Travaux de rénovation
                    </Text>
                    <Text fontSize="sm" color="brand.600" fontWeight="bold">
                      92% Pour
                    </Text>
                  </HStack>
                  <Box w="full" h={2} bg={"gray.300"} borderRadius="full">
                    <Box w="92%" h="full" bg="brand.600" borderRadius="full" />
                  </Box>
                </Box>
                <Box p={4} bg={"gray.100"} borderRadius="lg">
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" fontWeight="medium">
                      Résolution n°9 - Budget prévisionnel 2025
                    </Text>
                    <Text fontSize="sm" color="brand.600" fontWeight="bold">
                      85% Pour
                    </Text>
                  </HStack>
                  <Box w="full" h={2} bg={"gray.300"} borderRadius="full">
                    <Box w="85%" h="full" bg="brand.600" borderRadius="full" />
                  </Box>
                </Box>
              </VStack>
            </VStack>
          </Box>

              {/* Mockup 2: Accounting */}
              <Box
                p={8}
                bg={"gradient-to-br from-white to-gray.50"}
                borderRadius="2xl"
                border="1px solid"
                borderColor={"gray.200"}
                w="full"
                maxH="70vh"
                overflowY="auto"
                position="absolute"
                display="flex"
                alignItems="center"
                opacity={activeFeature === 1 ? 1 : 0}
                pointerEvents={activeFeature === 1 ? "auto" : "none"}
                transition="opacity 0.5s ease-in-out"
                boxShadow={"0 8px 32px rgba(0, 0, 0, 0.1)"}
              >
            <VStack align="stretch" gap={6} w="full">
              {/* Header */}
              <HStack justify="space-between" p={5} bg={bg.mockup} borderRadius="xl">
                <HStack gap={3}>
                  <Box p={2} bg="brand.200" borderRadius="lg">
                    <DollarSign size={20} color="#10b981" />
                  </Box>
                  <VStack align="start" gap={0}>
                    <Text fontWeight="bold" fontSize="lg">
                      Budget prévisionnel 2025
                    </Text>
                    <Text fontSize="sm" color={"gray.600"}>
                      Résidence Les Jardins
                    </Text>
                  </VStack>
                </HStack>
                <Badge colorPalette="brand" size="lg" px={4} py={2}>
                  Validé
                </Badge>
              </HStack>

              {/* Budget Cards */}
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <Box p={5} bg={bg.mockupCard} borderRadius="xl">
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" color={"gray.600"} fontWeight="medium">
                      Charges générales
                    </Text>
                    <Badge size="sm" colorPalette="blue">
                      Annuel
                    </Badge>
                  </HStack>
                  <Text fontSize="3xl" fontWeight="bold">
                    284 750 €
                  </Text>
                  <Text fontSize="xs" color="green.400" mt={1}>
                    +12% vs 2024
                  </Text>
                </Box>
                <Box p={5} bg={bg.mockupCard} borderRadius="xl">
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" color={"gray.600"} fontWeight="medium">
                      Travaux votés
                    </Text>
                    <Badge size="sm" colorPalette="purple">
                      Exceptionnel
                    </Badge>
                  </HStack>
                  <Text fontSize="3xl" fontWeight="bold">
                    125 000 €
                  </Text>
                  <Text fontSize="xs" color={text.tertiary} mt={1}>
                    Rénovation toiture
                  </Text>
                </Box>
              </Grid>

              {/* Appels de fonds */}
              <Box
                p={5}
                bg="brand.100"
                borderRadius="xl"
                border="1px solid"
                borderColor="brand.200"
              >
                <HStack justify="space-between" mb={3}>
                  <HStack gap={2}>
                    <Zap size={18} color="#10b981" />
                    <Text fontSize="sm" fontWeight="semibold" color="brand.600">
                      Appels de fonds automatiques
                    </Text>
                  </HStack>
                  <Text fontSize="sm" color={"gray.600"}>
                    T1 2025
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="sm" color={"gray.600"}>
                    142 copropriétaires • 4 échéances
                  </Text>
                  <Text fontSize="sm" fontWeight="bold" color={"gray.900"}>
                    102 438 €
                  </Text>
                </HStack>
              </Box>

              {/* Répartition */}
              <VStack align="stretch" gap={3}>
                <Text fontSize="sm" fontWeight="semibold" color={"gray.600"}>
                  Répartition par tantièmes
                </Text>
                <HStack gap={2}>
                  <Box flex={45} h={3} bg="blue.500" borderRadius="full" />
                  <Box flex={30} h={3} bg="purple.500" borderRadius="full" />
                  <Box flex={25} h={3} bg="emerald.500" borderRadius="full" />
                </HStack>
                <Grid templateColumns="repeat(3, 1fr)" gap={2} fontSize="xs">
                  <HStack gap={1}>
                    <Box w={2} h={2} bg="blue.500" borderRadius="full" />
                    <Text color={"gray.600"}>Générales 45%</Text>
                  </HStack>
                  <HStack gap={1}>
                    <Box w={2} h={2} bg="purple.500" borderRadius="full" />
                    <Text color={"gray.600"}>Chauffage 30%</Text>
                  </HStack>
                  <HStack gap={1}>
                    <Box w={2} h={2} bg="emerald.500" borderRadius="full" />
                    <Text color={"gray.600"}>Ascenseur 25%</Text>
                  </HStack>
                </Grid>
              </VStack>
            </VStack>
          </Box>

              {/* Mockup 3: Portal */}
              <Box
                p={8}
                bg={"gradient-to-br from-white to-gray.50"}
                borderRadius="2xl"
                border="1px solid"
                borderColor={"gray.200"}
                w="full"
                maxH="70vh"
                overflowY="auto"
                position="absolute"
                display="flex"
                alignItems="center"
                opacity={activeFeature === 2 ? 1 : 0}
                pointerEvents={activeFeature === 2 ? "auto" : "none"}
                transition="opacity 0.5s ease-in-out"
                boxShadow={"0 8px 32px rgba(0, 0, 0, 0.1)"}
              >
            <VStack align="stretch" gap={6} w="full">
              {/* Header */}
              <HStack justify="space-between" p={5} bg={bg.mockup} borderRadius="xl">
                <HStack gap={3}>
                  <Box p={2} bg="brand.200" borderRadius="lg">
                    <Users size={20} color="#10b981" />
                  </Box>
                  <VStack align="start" gap={0}>
                    <Text fontWeight="bold" fontSize="lg">
                      Espace copropriétaire
                    </Text>
                    <Text fontSize="sm" color={"gray.600"}>
                      M. Dupont • Lot B2-305
                    </Text>
                  </VStack>
                </HStack>
                <Badge colorPalette="green" size="lg" px={4} py={2}>
                  À jour
                </Badge>
              </HStack>

              {/* Quick Stats */}
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <Box p={5} bg={bg.mockupCard} borderRadius="xl">
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" color={"gray.600"} fontWeight="medium">
                      Solde compte
                    </Text>
                    <Box p={1} bg="green.500/20" borderRadius="md">
                      <CheckCircle size={14} color="#10b981" />
                    </Box>
                  </HStack>
                  <Text fontSize="3xl" fontWeight="bold" color="green.400">
                    0 €
                  </Text>
                  <Text fontSize="xs" color={text.tertiary} mt={1}>
                    Tous les appels réglés
                  </Text>
                </Box>
                <Box p={5} bg={bg.mockupCard} borderRadius="xl">
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" color={"gray.600"} fontWeight="medium">
                      Tantièmes
                    </Text>
                    <Badge size="sm" colorPalette="blue">
                      Lot 305
                    </Badge>
                  </HStack>
                  <Text fontSize="3xl" fontWeight="bold">
                    4,25%
                  </Text>
                  <Text fontSize="xs" color={text.tertiary} mt={1}>
                    425/10000èmes
                  </Text>
                </Box>
              </Grid>

              {/* Menu Items */}
              <VStack align="stretch" gap={3} mt={2}>
                <Box
                  p={4}
                  bg={"gray.100"}
                  borderRadius="xl"
                  _hover={{ bg: "gray.200" }}
                  transition="all 0.2s"
                  cursor="pointer"
                >
                  <HStack justify="space-between">
                    <HStack gap={3}>
                      <Box p={2} bg="blue.500/20" borderRadius="lg">
                        <FileText size={18} color="#3b82f6" />
                      </Box>
                      <VStack align="start" gap={0}>
                        <Text fontSize="sm" fontWeight="semibold">
                          Mes documents
                        </Text>
                        <Text fontSize="xs" color={"gray.600"}>
                          PV, convocations, factures
                        </Text>
                      </VStack>
                    </HStack>
                    <Badge size="sm" colorPalette="brand">
                      24 documents
                    </Badge>
                  </HStack>
                </Box>

                <Box
                  p={4}
                  bg={"gray.100"}
                  borderRadius="xl"
                  _hover={{ bg: "gray.200" }}
                  transition="all 0.2s"
                  cursor="pointer"
                >
                  <HStack justify="space-between">
                    <HStack gap={3}>
                      <Box p={2} bg="purple.500/20" borderRadius="lg">
                        <Calendar size={18} color="#a855f7" />
                      </Box>
                      <VStack align="start" gap={0}>
                        <Text fontSize="sm" fontWeight="semibold">
                          Prochaine AG
                        </Text>
                        <Text fontSize="xs" color={"gray.600"}>
                          Vote en ligne disponible
                        </Text>
                      </VStack>
                    </HStack>
                    <Text fontSize="sm" fontWeight="bold" color={"gray.900"}>
                      15 mars 2025
                    </Text>
                  </HStack>
                </Box>

                <Box
                  p={4}
                  bg={"gray.100"}
                  borderRadius="xl"
                  _hover={{ bg: "gray.200" }}
                  transition="all 0.2s"
                  cursor="pointer"
                >
                  <HStack justify="space-between">
                    <HStack gap={3}>
                      <Box p={2} bg="emerald.500/20" borderRadius="lg">
                        <DollarSign size={18} color="#10b981" />
                      </Box>
                      <VStack align="start" gap={0}>
                        <Text fontSize="sm" fontWeight="semibold">
                          Mes paiements
                        </Text>
                        <Text fontSize="xs" color={"gray.600"}>
                          Historique et échéances
                        </Text>
                      </VStack>
                    </HStack>
                    <Text fontSize="sm" color="green.400" fontWeight="bold">
                      À jour
                    </Text>
                  </HStack>
                </Box>
              </VStack>
            </VStack>
          </Box>

              {/* Mockup 4: Platform */}
              <Box
                p={8}
                bg={"gradient-to-br from-white to-gray.50"}
                borderRadius="2xl"
                border="1px solid"
                borderColor={"gray.200"}
                w="full"
                maxH="70vh"
                overflowY="auto"
                position="absolute"
                display="flex"
                alignItems="center"
                opacity={activeFeature === 3 ? 1 : 0}
                pointerEvents={activeFeature === 3 ? "auto" : "none"}
                transition="opacity 0.5s ease-in-out"
                boxShadow={"0 8px 32px rgba(0, 0, 0, 0.1)"}
              >
            <VStack align="stretch" gap={6} w="full">
              {/* Header */}
              <HStack justify="space-between" p={5} bg={bg.mockup} borderRadius="xl">
                <HStack gap={3}>
                  <Box p={2} bg="brand.200" borderRadius="lg">
                    <Zap size={20} color="#10b981" />
                  </Box>
                  <VStack align="start" gap={0}>
                    <Text fontWeight="bold" fontSize="lg">
                      Plateforme Kopro
                    </Text>
                    <Text fontSize="sm" color={"gray.600"}>
                      SaaS Full Web
                    </Text>
                  </VStack>
                </HStack>
                <Badge colorPalette="brand" size="lg" px={4} py={2}>
                  En ligne
                </Badge>
              </HStack>

              {/* Features Grid */}
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <Box p={5} bg={bg.mockupCard} borderRadius="xl">
                  <HStack gap={3} mb={3}>
                    <Box p={2} bg="blue.500/20" borderRadius="lg">
                      <Shield size={20} color="#3b82f6" />
                    </Box>
                    <Text fontSize="sm" fontWeight="bold">
                      Sécurité maximale
                    </Text>
                  </HStack>
                  <Text fontSize="xs" color={"gray.600"}>
                    Données hébergées en France, chiffrées et sauvegardées quotidiennement
                  </Text>
                </Box>

                <Box p={5} bg={bg.mockupCard} borderRadius="xl">
                  <HStack gap={3} mb={3}>
                    <Box p={2} bg="purple.500/20" borderRadius="lg">
                      <Sparkles size={20} color="#a855f7" />
                    </Box>
                    <Text fontSize="sm" fontWeight="bold">
                      Interface intuitive
                    </Text>
                  </HStack>
                  <Text fontSize="xs" color={"gray.600"}>
                    Aucune formation nécessaire, prise en main immédiate
                  </Text>
                </Box>

                <Box p={5} bg={bg.mockupCard} borderRadius="xl">
                  <HStack gap={3} mb={3}>
                    <Box p={2} bg="emerald.500/20" borderRadius="lg">
                      <Clock size={20} color="#10b981" />
                    </Box>
                    <Text fontSize="sm" fontWeight="bold">
                      Accessible 24/7
                    </Text>
                  </HStack>
                  <Text fontSize="xs" color={"gray.600"}>
                    100% web, accessible depuis n'importe quel navigateur
                  </Text>
                </Box>

                <Box p={5} bg={bg.mockupCard} borderRadius="xl">
                  <HStack gap={3} mb={3}>
                    <Box p={2} bg="orange.500/20" borderRadius="lg">
                      <BarChart3 size={20} color="#f97316" />
                    </Box>
                    <Text fontSize="sm" fontWeight="bold">
                      Mises à jour auto
                    </Text>
                  </HStack>
                  <Text fontSize="xs" color={"gray.600"}>
                    Zéro installation, zéro maintenance à gérer
                  </Text>
                </Box>
              </Grid>

              {/* Stats */}
              <Box
                p={5}
                bg="brand.100"
                borderRadius="xl"
                border="1px solid"
                borderColor="brand.200"
              >
                <Grid templateColumns="repeat(3, 1fr)" gap={4} textAlign="center">
                  <Box>
                    <Text fontSize="3xl" fontWeight="bold" color="brand.600">
                      99.9%
                    </Text>
                    <Text fontSize="xs" color={"gray.600"} mt={1}>
                      Disponibilité
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="3xl" fontWeight="bold" color="brand.600">
                      &lt;200ms
                    </Text>
                    <Text fontSize="xs" color={"gray.600"} mt={1}>
                      Temps de réponse
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="3xl" fontWeight="bold" color="brand.600">
                      100%
                    </Text>
                    <Text fontSize="xs" color={"gray.600"} mt={1}>
                      Conforme RGPD
                    </Text>
                  </Box>
                </Grid>
              </Box>
            </VStack>
          </Box>
            </Box>
          </Box>
          </Grid>
        </Container>

        <style>
          {`
            @keyframes fadeIn {
              from {
                opacity: 0;
                transform: translateY(10px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}
        </style>
      </Box>
    )
}

interface StatCardProps {
  label: string
  value: string
  trend: string
  icon: React.ReactNode
  bg: BackgroundColors
  text: TextColors
  border: BorderColors
}

function StatCard({ label, value, trend, icon, bg, border }: StatCardProps) {
  return (
    <Box
      p={6}
      bg={bg.mockupCard}
      borderRadius="xl"
      border="1px solid"
      borderColor={border.subtle}
    >
      <HStack justify="space-between" mb={4}>
        <Box color="brand.600">{icon}</Box>
        <HStack gap={1} color="green.400" fontSize="sm" fontWeight="medium">
          <TrendingUp size={16} />
          <Text>{trend}</Text>
        </HStack>
      </HStack>
      <Text fontSize="3xl" fontWeight="bold" mb={1}>
        {value}
      </Text>
      <Text fontSize="sm" color={"gray.600"}>
        {label}
      </Text>
    </Box>
  )
}

interface ActivityItemProps {
  text: string
  time: string
  textColors: TextColors
}

function ActivityItem({ text: itemText, time, textColors }: ActivityItemProps) {
  return (
    <HStack gap={3} align="start">
      <Box w={2} h={2} borderRadius="full" bg="brand.600" mt={2} flexShrink={0} />
      <Box flex={1}>
        <Text fontSize="sm" color={textColors.secondary} mb={0.5}>
          {itemText}
        </Text>
        <Text fontSize="xs" color={textColors.tertiary}>
          {time}
        </Text>
      </Box>
    </HStack>
  )
}

// ========================================
// DATA
// ========================================

const features = [
  {
    icon: <Building2 size={28} />,
    title: 'Multi-agence',
    description:
      'Architecture multi-tenant pour gérer plusieurs agences et leurs portefeuilles de copropriétés depuis une seule plateforme. Isolation des données garantie.',
  },
  {
    icon: <Users size={28} />,
    title: 'Équipes synchronisées',
    description:
      'Conçu pour les équipes de gestionnaires et assistants. Rôles adaptés (Admin, Manager, User), permissions granulaires, collaboration temps réel.',
  },
  {
    icon: <FileText size={28} />,
    title: 'GED complète',
    description:
      'Gestion électronique de documents intelligente. Organisation automatique par résidence, copropriétaire, moteur de recherche avancé, versioning, archivage.',
  },
  {
    icon: <Zap size={28} />,
    title: 'Services digitaux connectés',
    description:
      'Visio pour les AG, LRE/LRAR pour envois postaux digitalisés, signature électronique pour votes, agents IA pour génération de documents. Tout intégré.',
  },
  {
    icon: <Sparkles size={28} />,
    title: 'Personnalisation totale',
    description:
      'Customisez votre intranet et extranet : couleurs, logo, sous-domaine personnalisé. Votre identité de marque, notre technologie.',
  },
  {
    icon: <DollarSign size={28} />,
    title: 'Paiements intégrés',
    description:
      'Module de paiement en ligne pour les copropriétaires. Appels de fonds payables directement depuis l\'extranet. Suivi automatique des règlements.',
  },
]

const faqItems = [
  {
    question: 'Comment fonctionne l\'essai gratuit de 7 jours de Kopro ?',
    answer:
      'L\'essai gratuit de Kopro est totalement sans engagement et sans carte bancaire. Inscrivez-vous en quelques minutes et accédez immédiatement à l\'ensemble des fonctionnalités : gestion des assemblées générales, comptabilité de copropriété, extranet copropriétaires, et GED. Pendant 7 jours complets, vous pouvez créer vos résidences, importer vos données, et tester la plateforme dans des conditions réelles. Notre équipe support reste disponible pour vous accompagner. Si Kopro ne correspond pas à vos besoins, aucun paiement ne vous sera demandé. Vous choisissez votre formule uniquement si vous êtes convaincu.',
  },
  {
    question: 'Combien de temps prend la mise en place du logiciel Kopro ?',
    answer:
      'La mise en place de Kopro est conçue pour être rapide et efficace. Dès votre inscription, vous pouvez immédiatement utiliser la plateforme et créer votre première résidence. Pour une installation complète avec migration de vos données existantes (résidences, lots, copropriétaires, historique des documents), notre équipe vous accompagne gratuitement et le processus prend généralement 2 à 3 jours ouvrés. Nous nous adaptons à votre rythme et à votre charge de travail. L\'import de données se fait de manière sécurisée depuis vos fichiers Excel, CSV ou exports de votre logiciel actuel. Notre équipe technique gère l\'intégralité du processus pour vous garantir une transition en douceur.',
  },
  {
    question: 'Puis-je importer mes données de copropriété existantes dans Kopro ?',
    answer:
      'Oui, l\'import de vos données existantes est entièrement pris en charge par Kopro. Notre équipe technique vous accompagne gratuitement pour migrer l\'ensemble de vos informations : liste des résidences et lots, coordonnées des copropriétaires, tantièmes et clés de répartition, historique comptable, et documents archivés. Nous acceptons tous les formats courants : fichiers Excel, CSV, exports depuis votre logiciel de syndic actuel. Le processus est sécurisé et nous garantissons l\'intégrité de vos données. Aucune perte d\'information, aucune ressaisie manuelle. En 2-3 jours, toutes vos données sont structurées et accessibles dans Kopro. Vous bénéficiez également d\'une formation personnalisée pour prendre en main rapidement votre nouvel environnement de travail.',
  },
  {
    question: 'Mes données de copropriété sont-elles sécurisées avec Kopro ?',
    answer:
      'La sécurité de vos données est notre priorité absolue. Kopro respecte les standards les plus exigeants : hébergement en France avec certification HDS (Hébergeur de Données de Santé), chiffrement AES-256 en transit et au repos, sauvegardes quotidiennes automatiques avec rétention de 30 jours, conformité RGPD stricte avec DPO dédié, et authentification sécurisée multi-facteurs disponible. Vos données sont cloisonnées par agence et résidence, garantissant une isolation totale entre clients. Nous réalisons des audits de sécurité réguliers et des tests d\'intrusion. Vous conservez la propriété intégrale de vos données et pouvez les exporter à tout moment. Nos infrastructures sont surveillées 24/7 et nos équipes interviennent immédiatement en cas d\'incident. Kopro vous offre un niveau de sécurité bancaire pour protéger les informations sensibles de vos copropriétés.',
  },
  {
    question: 'Kopro est-il conforme à la loi ALUR et à la loi ÉLAN pour les syndics ?',
    answer:
      'Kopro est 100% conforme aux réglementations en vigueur pour les syndics de copropriété, notamment les lois ALUR (2014) et ÉLAN (2018). La plateforme intègre nativement toutes les obligations légales : tenue du registre d\'immatriculation des copropriétés, gestion du fonds de travaux obligatoire, respect des délais de convocation aux assemblées générales (21 jours minimum), organisation des votes selon les modalités légales (majorité simple, absolue, double majorité), information préalable des copropriétaires, et conservation des documents officiels. Kopro facilite également la mise en œuvre du vote par correspondance et du vote électronique à distance, conformément aux dispositions légales. Notre équipe juridique veille en permanence aux évolutions réglementaires et met à jour automatiquement la plateforme pour garantir votre conformité. Vous exercez votre activité de syndic en toute sérénité, sans risque de non-conformité.',
  },
]
